import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '0 Vxu';
  return Number(amount).toLocaleString('vi-VN') + ' Vxu';
};

const SUGGESTED_AMOUNTS = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

function TopupAgencyModal({ open, onClose, agencySelected, onRefresh }) {
  const classes = useStyles();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleAmountChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setAmount(val);
    setError('');
  };

  const handleSuggestClick = (val) => {
    setAmount(String(val));
    setError('');
  };

  const handleTopup = async () => {
    const numAmount = Number(amount);
    if (!amount || numAmount <= 0) {
      setError('Vui lòng nhập số Vxu hợp lệ');
      return;
    }

    setLoading(true);
    setError('');
    
    // Create a raw axios instance to bypass response interceptors (no 403 redirect)
    const rawAxios = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access-token')}`
      }
    });

    try {
      let response;
      let paymentCode = '';

      // POST /QRCode/topup/admin
      try {
        response = await rawAxios.post('/QRCode/topup/admin', {
          owner_type: 'agency',
          owner_id: Number(agencySelected.id),
          amount: numAmount
        });
        paymentCode = response.data?.payload?.result?.payment_code || response.data?.payload?.payment_code || response.data?.payment_code;
      } catch (err) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message || err?.message || 'Không thể kết nối máy chủ';
        throw new Error(`[Lỗi ${status || 'Tạo mã'}] ${msg}`);
      }

      if (!paymentCode) {
        throw new Error('Không thể tạo mã giao dịch nạp Vxu (thiếu payment_code)');
      }

      // Auto confirm the transaction for free topup
      let confirmResponse;
      try {
        confirmResponse = await rawAxios.post('/QRCode/confirm-payment', {
          payment_code: paymentCode
        });
      } catch (err) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message || err?.message || 'Không thể xác nhận giao dịch';
        throw new Error(`[Lỗi ${status || 'Xác nhận'}] ${msg}`);
      }

      if (confirmResponse.data?.success || confirmResponse.status === 200) {
        setSuccess(true);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setError(confirmResponse.data?.message || 'Không thể xác nhận giao dịch nạp Vxu');
      }
    } catch (err) {
      setError(err.message || 'Có lỗi xảy ra khi nạp Vxu');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAmount('');
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="xs"
      fullWidth
      disableBackdropClick
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" style={{ fontWeight: 600, color: '#ffffff' }}>
          Nạp Vxu cho đại lý
        </Typography>
        <IconButton onClick={handleReset} size="small" className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={5}>
            <CircularProgress size={40} style={{ color: '#D9042F' }} />
            <Typography variant="body2" style={{ marginTop: 16, color: '#666' }}>
              Đang xử lý nạp Vxu cho đại lý...
            </Typography>
          </Box>
        ) : success ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <Box
              style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, boxShadow: '0 4px 20px rgba(46,125,50,0.3)'
              }}
            >
              <Typography style={{ color: '#fff', fontSize: 32 }}>✓</Typography>
            </Box>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#2e7d32', marginBottom: 8 }}>
              Nạp Vxu thành công!
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Đại lý: <b>{agencySelected?.name || ''}</b>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8, color: '#666' }}>
              Số Vxu đã cộng: <b style={{ color: '#2e7d32' }}>+{formatCurrency(Number(amount))}</b>
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box mb={2} style={{ background: '#f5f5f5', padding: '10px 14px', borderRadius: '8px' }}>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Đại lý nhận Vxu:{' '}
                <span style={{ fontWeight: 700, color: '#D9042F' }}>
                  {agencySelected?.name || ''}
                </span>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Tài khoản: {agencySelected?.username || agencySelected?.phone || ''}
              </Typography>
            </Box>

            <InputLabel style={{ marginBottom: 8, fontWeight: 500, color: '#333' }}>
              Số Vxu muốn nạp <span className="text-danger">*</span>
            </InputLabel>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              placeholder="Nhập số Vxu..."
              value={amount ? Number(amount).toLocaleString('vi-VN') : ''}
              onChange={handleAmountChange}
              error={!!error}
              helperText={error || (amount ? `= ${(Number(amount) * 1000).toLocaleString('vi-VN')} VNĐ` : '')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" style={{ color: '#888', fontWeight: 600 }}>Vxu</Typography>
                  </InputAdornment>
                )
              }}
              className={classes.textField}
            />

            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
              Gợi ý số Vxu nạp:
            </Typography>

            <Grid container spacing={1}>
              {SUGGESTED_AMOUNTS.map((val) => (
                <Grid item xs={4} key={val}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.suggestBtn}
                    onClick={() => handleSuggestClick(val)}
                  >
                    <Box textAlign="center" style={{ lineHeight: 1.3, padding: '2px 0' }}>
                      <Typography style={{ fontSize: 12, fontWeight: 600, color: 'inherit' }}>
                        {Number(val).toLocaleString('vi-VN')} Vxu
                      </Typography>
                      <Typography style={{ fontSize: 10.5, color: '#666', opacity: 0.9 }}>
                        ({(val * 1000).toLocaleString('vi-VN')} vnđ)
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px' }}>
        {success ? (
          <Button
            variant="contained"
            onClick={handleReset}
            fullWidth
            style={{ textTransform: 'none', height: 40, backgroundColor: '#2e7d32', color: '#fff' }}
          >
            Đóng
          </Button>
        ) : (
          <>
            <Button
              onClick={handleReset}
              disabled={loading}
              style={{ textTransform: 'none', color: '#666' }}
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              onClick={handleTopup}
              disabled={loading || !amount}
              style={{
                textTransform: 'none',
                minWidth: 150,
                height: 40,
                backgroundColor: loading || !amount ? undefined : '#D9042F',
                color: loading || !amount ? undefined : '#fff'
              }}
            >
              {loading ? 'Đang xử lý...' : 'Xác nhận nạp'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#D9042F',
    color: '#fff'
  },
  closeButton: {
    color: '#fff'
  },
  dialogContent: {
    padding: '24px'
  },
  textField: {
    marginBottom: '16px',
    '& input': {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333'
    }
  },
  suggestBtn: {
    textTransform: 'none',
    borderColor: '#e0e0e0',
    padding: '4px 2px',
    borderRadius: '8px',
    '&:hover': {
      borderColor: '#D9042F',
      color: '#D9042F',
      backgroundColor: '#fff5f5'
    }
  }
}));

export default TopupAgencyModal;
